// Email service using Resend.com API
export class EmailService {
  private static instance: EmailService;
  private apiKey: string;

  private constructor() {
    this.apiKey = process.env.REACT_APP_RESEND_API_KEY || '';
  }

  public static getInstance(): EmailService {
    if (!EmailService.instance) {
      EmailService.instance = new EmailService();
    }
    return EmailService.instance;
  }

  // Send vendor order notification
  public async sendVendorOrderNotification(
    vendorEmail: string,
    vendorName: string,
    orderData: {
      orderId: string;
      customerName: string;
      customerEmail: string;
      items: Array<{
        name: string;
        quantity: number;
        price: number;
        total: number;
      }>;
      subtotal: number;
      shippingCost: number;
      total: number;
      vendorEarnings: number;
      platformFee: number;
      shippingAddress: {
        street: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
      };
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/send-vendor-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendorEmail,
          vendorName,
          orderData,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send vendor notification');
      }

      return { success: true };
    } catch (error) {
      console.error('Error sending vendor notification:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  public async sendVendorLowStockNotification(
    vendorEmail: string,
    vendorName: string,
    productData: {
      productName: string;
      sku: string;
      quantity: number;
      threshold: number;
      productId?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/send-vendor-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notificationType: 'low-stock',
          vendorEmail,
          vendorName,
          productData,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send low stock notification');
      }

      return { success: true };
    } catch (error) {
      console.error('Error sending low stock notification:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Send customer order confirmation
  public async sendCustomerOrderConfirmation(
    customerEmail: string,
    customerName: string,
    orderData: {
      orderId: string;
      totalAmount: number;
      shippingAddress: {
        street: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
      };
      vendorOrders: Array<{
        vendorName: string;
        items: Array<{
          name: string;
          quantity: number;
          price: number;
        }>;
        subtotal: number;
        shippingCost: number;
        total: number;
      }>;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/send-customer-confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerEmail,
          customerName,
          orderData,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send customer confirmation');
      }

      return { success: true };
    } catch (error) {
      console.error('Error sending customer confirmation:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Send order status update
  public async sendOrderStatusUpdate(
    customerEmail: string,
    customerName: string,
    orderData: {
      orderId: string;
      status: string;
      trackingNumber?: string;
      vendorName: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/send-order-status-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerEmail,
          customerName,
          orderData,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send order status update');
      }

      return { success: true };
    } catch (error) {
      console.error('Error sending order status update:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}

export default EmailService;

